import React from 'react';
import { Bell, RefreshCw, X, EyeOff, MoreVertical, List, Trash2 } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useLocation } from 'wouter';

export function NotificationBell({ className }: { className?: string }) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications, hideNotification, refreshNotifications, isLoading } = useNotifications();
  const [open, setOpen] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [, setLocation] = useLocation();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshNotifications();
    } finally {
      setIsRefreshing(false);
    }
  };

  const displayedNotifications = notifications.slice(0, 3);
  const hasMore = notifications.length > 3;
  
  // Usar unreadCount para o badge (apenas não lidas)
  const displayCount = unreadCount > 99 ? '99+' : unreadCount.toString();

  const handleNotificationClick = (notification: any) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.link) {
      setLocation(notification.link);
      setOpen(false);
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteNotification(id);
  };

  const handleHide = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    hideNotification(id);
  };

  const handleOpenChange = async (newOpen: boolean) => {
    setOpen(newOpen);
    // Ao abrir o popover, marcar todas as notificações como lidas automaticamente
    if (newOpen && unreadCount > 0) {
      await markAllAsRead();
    }
  };

  const handleClearAll = async () => {
    if (confirm('Tem certeza que deseja excluir todas as notificações?')) {
      await deleteAllNotifications();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className={cn("relative h-9 w-9", className)}>
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] min-w-[1.125rem] h-4 flex items-center justify-center rounded-full px-0.5 leading-none"
            >
              {displayCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h4 className="font-semibold">Notificações</h4>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setOpen(false);
                setLocation('/notifications');
              }}
              className="h-7 w-7"
              title="Ver todas as notificações"
            >
              <List className="h-3 w-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleClearAll}
              disabled={notifications.length === 0}
              className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
              title="Limpar todas as notificações"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleRefresh} 
              disabled={isRefreshing || isLoading}
              className="h-7 w-7"
              title="Atualizar notificações"
            >
              <RefreshCw className={cn("h-3 w-3", (isRefreshing || isLoading) && "animate-spin")} />
            </Button>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={() => markAllAsRead()} className="text-xs h-auto py-1">
                Marcar todas como lidas
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[280px] overflow-hidden flex flex-col">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              Nenhuma notificação
            </div>
          ) : (
            <>
              <div className="divide-y overflow-y-auto flex-1">
                {displayedNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-2.5 hover:bg-muted/50 transition-colors cursor-pointer group relative",
                      !notification.read && "bg-muted/20"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex justify-between items-start gap-2 pr-8">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn("text-xs font-medium truncate", !notification.read && "text-primary")}>
                            {notification.title}
                          </p>
                          <span className="text-[9px] text-muted-foreground whitespace-nowrap flex-shrink-0">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: ptBR })}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1 flex-shrink-0" />
                      )}
                    </div>
                    <div className="absolute right-1 top-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-muted-foreground hover:text-destructive"
                        onClick={(e) => handleDelete(e, notification.id)}
                        title="Excluir"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-muted-foreground hover:text-foreground"
                        onClick={(e) => handleHide(e, notification.id)}
                        title="Ocultar"
                      >
                        <EyeOff className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div className="border-t p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => {
                      setOpen(false);
                      setLocation('/notifications');
                    }}
                  >
                    Ver Mais
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
